import React, { useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import BackButton from '../mobile-menu-item/BackButton';
import MobileMenuItem from '../mobile-menu-item/MobileMenuItem';
import styles from './MobileMenu.scss';
import NestedMenu from './NestedMenu';

const SubMenu = ({
  open,
  subMenuData,
  onNavigateBack,
  onItemSelected,
  scrollToTop,
  hierarchy: parentHierarchy,
  level,
  children,
}) => {
  const [activeIndex, updateActiveIndex] = useState(-1);
  const offScreenLeft = activeIndex !== -1;
  const hierarchy = level > 0 ? [...parentHierarchy, subMenuData.name] : [];

  const cn = classNames(styles.subMenu, {
    [styles.opened]: open,
    [styles.offScreenLeft]: offScreenLeft,
  });

  const childNavigateBack = () => {
    scrollToTop();
    updateActiveIndex(-1);
  };

  return (
    <>
      <div className={cn} role="menu">
        {subMenuData.backLabel && (
          <BackButton label={subMenuData.backLabel} onClick={onNavigateBack} />
        )}
        <h3 className={styles.mobileMenu__name}>{subMenuData.name}</h3>
        <ul className={styles.mobileMenuList}>
          {subMenuData?.child?.map((menuItem, index) => {
            const { id, name, url, child } = menuItem;
            return (
              <MobileMenuItem
                key={id}
                label={name}
                url={url}
                onClick={() => {
                  scrollToTop();
                  updateActiveIndex(index);
                  if (typeof onItemSelected === 'function') {
                    onItemSelected(menuItem.id, index);
                  }
                }}
                child={child}
                hierarchy={hierarchy}
              />
            );
          })}
        </ul>
        {children}
      </div>
      <SubMenuChild
        {...{
          activeIndex,
          subMenuData,
          level,
          scrollToTop,
          hierarchy,
          childNavigateBack,
        }}
      />
    </>
  );
};

function SubMenuChild({
  activeIndex,
  subMenuData,
  level,
  scrollToTop,
  hierarchy,
  childNavigateBack,
}) {
  const childOpen = activeIndex !== -1;
  const childData =
    activeIndex === -1
      ? { name: '' }
      : {
          backLabel: subMenuData.name,
          ...subMenuData.child[activeIndex],
        };

  return level === 2 ? (
    <NestedMenu
      open={childOpen}
      data={childData}
      onNavigateBack={childNavigateBack}
      hierarchy={hierarchy}
    />
  ) : (
    <SubMenu
      level={level + 1}
      open={childOpen}
      subMenuData={childData}
      scrollToTop={scrollToTop}
      onNavigateBack={childNavigateBack}
      hierarchy={hierarchy}
    />
  );
}

const itemShape = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  url: PropTypes.string,
};
itemShape.child = PropTypes.arrayOf(PropTypes.shape(itemShape));

SubMenu.defaultProps = {
  onNavigateBack: undefined,
  onItemSelected: undefined,
  children: undefined,
};

SubMenu.propTypes = {
  open: PropTypes.bool.isRequired,
  subMenuData: PropTypes.shape({
    name: PropTypes.string.isRequired,
    backLabel: PropTypes.string,
    child: PropTypes.arrayOf(PropTypes.shape(itemShape)),
  }).isRequired,
  onNavigateBack: PropTypes.func,
  onItemSelected: PropTypes.func,
  hierarchy: PropTypes.arrayOf(PropTypes.string).isRequired,
  level: PropTypes.number.isRequired,
  children: PropTypes.node,
};

export default SubMenu;
